'use client';
import React from 'react';
import PropTypes from 'prop-types';
import useEmblaCarousel from 'embla-carousel-react';
import { theme } from '@ui/theme';
import { GlassCarouselArrow, usePrevNextButtons } from '@web/molecules';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import {
  CardsGrid,
  CardsScrollRow,
  CardsScrollSpacer,
  MobileCardSlot,
  DesktopCarouselWrapper,
  CarouselViewport,
  CarouselTrack,
  CarouselSlide,
} from './styles.js';

const MobileStoryCardsRow = ({ children, singleCard = false }) => (
  <CardsScrollRow
    $desktopScroll={false}
    $centerSingleCard={singleCard}
    role="list"
    aria-label="Stories"
  >
    {!singleCard && <CardsScrollSpacer $snap aria-hidden="true" />}
    {children.map((child, index) => (
      <MobileCardSlot
        key={child.key != null ? String(child.key) : `story-card-${index}`}
        role="listitem"
        $desktopScroll={false}
        $centerSingleCard={singleCard}
        $snapAlign={
          index === 0
            ? 'start'
            : index === children.length - 1
            ? 'end'
            : 'center'
        }
      >
        {child}
      </MobileCardSlot>
    ))}
    {!singleCard && <CardsScrollSpacer aria-hidden="true" />}
  </CardsScrollRow>
);

MobileStoryCardsRow.propTypes = {
  children: PropTypes.arrayOf(PropTypes.node).isRequired,
  singleCard: PropTypes.bool,
};

const DesktopStoryCardsGrid = ({ cardCount, children }) => (
  <CardsGrid $count={cardCount} role="list" aria-label="Stories">
    {children.map((child, index) => (
      <div
        key={child.key != null ? String(child.key) : `story-card-${index}`}
        role="listitem"
      >
        {child}
      </div>
    ))}
  </CardsGrid>
);

DesktopStoryCardsGrid.propTypes = {
  cardCount: PropTypes.number.isRequired,
  children: PropTypes.arrayOf(PropTypes.node).isRequired,
};

const DesktopStoryCardsEmblaCarousel = ({ children }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    containScroll: 'trimSnaps',
    dragFree: false,
  });

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(emblaApi);

  return (
    <DesktopCarouselWrapper $enabled>
      <GlassCarouselArrow
        direction="prev"
        onClick={onPrevButtonClick}
        disabled={prevBtnDisabled}
        aria-label="Previous cards"
        iconColor="text.lightest"
      />
      <CarouselViewport ref={emblaRef}>
        <CarouselTrack>
          {children.map((child, index) => (
            <CarouselSlide
              key={
                child.key != null ? String(child.key) : `story-card-${index}`
              }
              role="listitem"
            >
              {child}
            </CarouselSlide>
          ))}
        </CarouselTrack>
      </CarouselViewport>
      <GlassCarouselArrow
        direction="next"
        onClick={onNextButtonClick}
        disabled={nextBtnDisabled}
        aria-label="Next cards"
        iconColor="text.lightest"
      />
    </DesktopCarouselWrapper>
  );
};

DesktopStoryCardsEmblaCarousel.propTypes = {
  children: PropTypes.arrayOf(PropTypes.node).isRequired,
};

export const StoryCardsCarousel = ({ cardCount, children }) => {
  const isLaptop = useMediaQuery(theme.laptop);
  const childArray = React.Children.toArray(children);

  if (!isLaptop) {
    return (
      <MobileStoryCardsRow singleCard={cardCount === 1}>
        {childArray}
      </MobileStoryCardsRow>
    );
  }

  if (cardCount <= 3) {
    return (
      <DesktopStoryCardsGrid cardCount={cardCount}>
        {childArray}
      </DesktopStoryCardsGrid>
    );
  }

  return (
    <DesktopStoryCardsEmblaCarousel>
      {childArray}
    </DesktopStoryCardsEmblaCarousel>
  );
};

StoryCardsCarousel.displayName = 'StoryCardsCarousel';

StoryCardsCarousel.propTypes = {
  cardCount: PropTypes.number.isRequired,
  children: PropTypes.node.isRequired,
};
